import { useState, useEffect } from 'react'
import { createPortal } from 'react-dom'
import { 
  Plus, 
  Edit, 
  Trash2, 
  Upload,
  X,
  Loader,
  Image as ImageIcon,
  RotateCcw,
  Video,
  Search
} from 'lucide-react'
import { collection, getDocs, addDoc, updateDoc, deleteDoc, doc, query, orderBy, limit, startAfter } from 'firebase/firestore'
import { ref, uploadBytesResumable, getDownloadURL, deleteObject } from 'firebase/storage'
import { db, storage, COLLECTIONS, STORAGE_PATHS } from '../firebase/config'
import PageHelpButton from '../components/PageHelpButton'
import toast from 'react-hot-toast'

const Gallery = () => {
  const PAGE_SIZE = 50
  const [galleryItems, setGalleryItems] = useState([])
  const [loading, setLoading] = useState(true)
  const [loadingMore, setLoadingMore] = useState(false)
  const [lastVisible, setLastVisible] = useState(null)
  const [hasMore, setHasMore] = useState(false)
  const [showAddModal, setShowAddModal] = useState(false)
  const [editingItem, setEditingItem] = useState(null)
  const [uploading, setUploading] = useState(false)
  const [uploadProgress, setUploadProgress] = useState(0)
  const [uploadPhase, setUploadPhase] = useState(null) // 'uploading' | 'saving' | null
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    category: 'Massage',
    isActive: true,
    imageUrl: '',
    mediaType: 'image' // 'image' or 'video'
  })
  const [imageFile, setImageFile] = useState(null)
  const [imagePreview, setImagePreview] = useState('')
  const [searchQuery, setSearchQuery] = useState('')

  // Spa~Radise Luxe 7 services (aligned with admin Services page)
  const categories = ['Massage', 'Facials', 'Wax', 'Body Scrubbing', 'Manicures', 'Pedicures', 'Combo Packages', 'Other']

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }))
  }

  const handleMediaSelect = (e) => {
    const file = e.target.files[0]
    if (file) {
      const isImage = file.type.startsWith('image/')
      const isVideo = file.type.startsWith('video/')
      
      if (!isImage && !isVideo) {
        toast.error('Please select an image or video file')
        return
      }

      // Different size limits for images and videos
      if (isImage && file.size > 10 * 1024 * 1024) {
        toast.error('Image size must be less than 10MB')
        return
      }
      if (isVideo && file.size > 100 * 1024 * 1024) {
        toast.error('Video size must be less than 100MB')
        return
      }

      setImageFile(file)
      setFormData(prev => ({
        ...prev,
        mediaType: isImage ? 'image' : 'video'
      }))

      const reader = new FileReader()
      reader.onloadend = () => {
        setImagePreview(reader.result)
      }
      reader.readAsDataURL(file)
    }
  }

  const uploadMedia = (file, onProgress) => {
    const timestamp = Date.now()
    const fileName = `${timestamp}_${file.name}`
    const storageRef = ref(storage, `${STORAGE_PATHS.GALLERY}/${fileName}`)
    const uploadTask = uploadBytesResumable(storageRef, file)
    return new Promise((resolve, reject) => {
      uploadTask.on(
        'state_changed',
        (snapshot) => {
          const pct = Math.round((snapshot.bytesTransferred / snapshot.totalBytes) * 100)
          onProgress(pct)
        },
        reject,
        async () => {
          const downloadURL = await getDownloadURL(uploadTask.snapshot.ref)
          resolve(downloadURL)
        }
      )
    })
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    
    if (!imageFile && !formData.imageUrl) {
      toast.error('Please select an image or video')
      return
    }

    setUploading(true)
    setUploadProgress(0)
    setUploadPhase(null)
    try {
      let imageUrl = formData.imageUrl
      let mediaType = formData.mediaType

      // Upload new media if selected (with progress)
      if (imageFile) {
        setUploadPhase('uploading')
        imageUrl = await uploadMedia(imageFile, (pct) => setUploadProgress(pct))
        mediaType = imageFile.type.startsWith('video/') ? 'video' : 'image'
        setUploadProgress(100)
      }
      setUploadPhase('saving')

      const galleryData = {
        title: formData.title,
        description: formData.description,
        category: formData.category,
        isActive: formData.isActive,
        imageUrl: imageUrl,
        mediaType: mediaType,
        createdAt: editingItem?.createdAt || new Date().toISOString(),
        updatedAt: new Date().toISOString()
      }

      if (editingItem) {
        // Delete old image if new one uploaded
        if (imageFile && editingItem.imageUrl) {
          try {
            const oldImageRef = ref(storage, editingItem.imageUrl)
            await deleteObject(oldImageRef)
          } catch (error) {
            console.error('Error deleting old image:', error)
          }
        }

        await updateDoc(doc(db, COLLECTIONS.GALLERY, editingItem.id), galleryData)
        toast.success('Gallery item updated successfully!')
        
        setGalleryItems(prev => prev.map(item => 
          item.id === editingItem.id 
            ? { ...galleryData, id: editingItem.id }
            : item
        ))
      } else {
        const docRef = await addDoc(collection(db, COLLECTIONS.GALLERY), galleryData)
        toast.success('Gallery item added successfully!')
        
        setGalleryItems(prev => [...prev, { ...galleryData, id: docRef.id }])
      }

      // Reset form
      setShowAddModal(false)
      setEditingItem(null)
      setFormData({
        title: '',
        description: '',
        category: 'Massage',
        isActive: true,
        imageUrl: '',
        mediaType: 'image'
      })
      setImageFile(null)
      setImagePreview('')
      setUploadPhase(null)
      setUploadProgress(0)
    } catch (error) {
      console.error('Error saving gallery item:', error)
      toast.error('Failed to save gallery item. Please try again.')
      setUploadPhase(null)
      setUploadProgress(0)
    } finally {
      setUploading(false)
    }
  }

  const handleEdit = (item) => {
    setEditingItem(item)
    setFormData({
      title: item.title || '',
      description: item.description || '',
      category: item.category || 'Massage',
      isActive: item.isActive !== undefined ? item.isActive : true,
      imageUrl: item.imageUrl || '',
      mediaType: item.mediaType || 'image'
    })
    setImagePreview(item.imageUrl || '')
    setImageFile(null)
    setShowAddModal(true)
  }

  const handleDelete = async (id, imageUrl) => {
    if (!window.confirm('Are you sure you want to delete this gallery item?')) {
      return
    }

    try {
      // Delete image from storage
      if (imageUrl) {
        try {
          const imageRef = ref(storage, imageUrl)
          await deleteObject(imageRef)
        } catch (error) {
          console.error('Error deleting image:', error)
        }
      }

      // Delete document from Firestore
      await deleteDoc(doc(db, COLLECTIONS.GALLERY, id))
      toast.success('Gallery item deleted successfully!')
      
      setGalleryItems(prev => prev.filter(item => item.id !== id))
    } catch (error) {
      console.error('Error deleting gallery item:', error)
      toast.error('Failed to delete gallery item. Please try again.')
    }
  }

  const fetchGalleryItems = async (cursor = null, append = false) => {
    try {
      if (append) setLoadingMore(true)
      else setLoading(true)
      const base = collection(db, COLLECTIONS.GALLERY)
      const q = cursor
        ? query(base, orderBy('createdAt', 'desc'), limit(PAGE_SIZE), startAfter(cursor))
        : query(base, orderBy('createdAt', 'desc'), limit(PAGE_SIZE))
      const snapshot = await getDocs(q)
      const items = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }))
      const lastDoc = snapshot.docs[snapshot.docs.length - 1] || null
      setLastVisible(lastDoc)
      setHasMore(snapshot.docs.length === PAGE_SIZE)
      if (append) setGalleryItems(prev => [...prev, ...items])
      else setGalleryItems(items)
    } catch (error) {
      console.error('Error fetching gallery items:', error)
      toast.error('Failed to load gallery items. Please try again.')
      setHasMore(false)
    } finally {
      if (append) setLoadingMore(false)
      else setLoading(false)
    }
  }

  const loadMoreGallery = () => {
    if (!lastVisible || loadingMore) return
    fetchGalleryItems(lastVisible, true)
  }

  useEffect(() => {
    fetchGalleryItems()
  }, [])

  const filteredItems = galleryItems.filter(item => {
    const q = (searchQuery || '').trim().toLowerCase()
    if (!q) return true
    const match = (s) => (s || '').toLowerCase().includes(q)
    return match(item.title) || match(item.description) || match(item.category)
  })

  return (
    <div className="space-y-6 text-white">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-white">Gallery Management</h1>
          <p className="text-gray-300 mt-1">Manage your spa gallery images</p>
        </div>
        <div className="flex items-center space-x-3 mt-4 sm:mt-0">
          <button
            onClick={() => fetchGalleryItems()}
            disabled={loading}
            className="flex items-center space-x-2 px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <RotateCcw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
            <span>Refresh</span>
          </button>
          <button
            onClick={() => {
              setShowAddModal(true)
              setEditingItem(null)
              setFormData({
                title: '',
                description: '',
                category: 'Massage',
                isActive: true,
                imageUrl: '',
                mediaType: 'image'
              })
              setImageFile(null)
              setImagePreview('')
            }}
            className="btn-primary flex items-center space-x-2"
          >
            <Plus className="w-5 h-5" />
            <span>Add New Media</span>
          </button>
        </div>
      </div>

      <PageHelpButton
        videoId={6}
        title="Gallery management tutorial"
        youtubeUrl="https://www.youtube.com/embed/pAhmW5CytYk"
      />

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
        <input
          type="text"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="Search by title, description, category..."
          className="w-full pl-9 pr-4 py-2.5 rounded-lg bg-gray-800 border border-gray-700 text-white placeholder-gray-500 focus:ring-2 focus:ring-purple-500"
        />
        {searchQuery.trim() && <span className="text-xs text-gray-400 ml-2">{filteredItems.length} result(s)</span>}
      </div>

      {/* Gallery Table */}
      {loading ? (
        <div className="flex justify-center items-center py-12">
          <Loader className="w-8 h-8 animate-spin text-gray-400" />
        </div>
      ) : filteredItems.length === 0 ? (
        <div className="text-center py-12 bg-gray-800 rounded-lg">
          <ImageIcon className="w-16 h-16 mx-auto text-gray-600 mb-4" />
          <p className="text-gray-400">No gallery items yet. Add your first image!</p>
        </div>
      ) : (
        <>
          <div className="bg-gray-800 rounded-lg border border-gray-700 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-700">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-300 uppercase w-20">Edit</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase">Preview</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase">Title</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase">Category</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase">Type</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase">Status</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-300 uppercase w-20">Delete</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-700">
                  {filteredItems.map((item) => (
                    <tr key={item.id} className="hover:bg-gray-700/50">
                      <td className="px-2 py-4">
                        <button type="button" onClick={() => handleEdit(item)} className="min-h-[44px] min-w-[44px] flex items-center justify-center p-2 rounded-lg bg-blue-600 hover:bg-blue-700 text-white touch-manipulation" title="Edit"><Edit className="w-5 h-5" /></button>
                      </td>
                      <td className="px-6 py-4">
                        <div className="w-20 h-14 rounded overflow-hidden bg-gray-700 flex-shrink-0">
                          {item.mediaType === 'video' ? (
                            <video src={item.imageUrl} className="w-full h-full object-cover" preload="metadata" />
                          ) : (
                            <img src={item.imageUrl} alt="" className="w-full h-full object-cover" onError={e => { e.target.src = 'https://via.placeholder.com/80x56?text=No+Image' }} />
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="font-medium text-white">{item.title || 'Untitled'}</div>
                        {item.description && <div className="text-xs text-gray-400 line-clamp-1 max-w-xs">{item.description}</div>}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-300">{item.category}</td>
                      <td className="px-6 py-4 text-sm text-gray-300">{item.mediaType === 'video' ? 'Video' : 'Image'}</td>
                      <td className="px-6 py-4">
                        <span className={`px-2 py-1 text-xs rounded-full ${item.isActive ? 'bg-green-600/80 text-white' : 'bg-red-600/80 text-white'}`}>{item.isActive ? 'Active' : 'Inactive'}</span>
                      </td>
                      <td className="px-2 py-4">
                        <button type="button" onClick={() => handleDelete(item.id, item.imageUrl)} className="min-h-[44px] min-w-[44px] flex items-center justify-center p-2 rounded-lg bg-red-600 hover:bg-red-700 text-white touch-manipulation" title="Delete"><Trash2 className="w-5 h-5" /></button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
          {!loading && galleryItems.length > 0 && (
            <div className="border-t border-gray-700 px-6 py-3 flex items-center justify-between bg-gray-800/50">
              <p className="text-xs text-gray-400">Load more only when needed to reduce reads.</p>
              <button
                type="button"
                onClick={loadMoreGallery}
                disabled={!hasMore || loadingMore}
                className="inline-flex items-center space-x-2 px-4 py-2 rounded-lg text-xs font-medium bg-gray-700 hover:bg-gray-600 text-white border border-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loadingMore ? (
                  <>
                    <Loader className="w-4 h-4 animate-spin" />
                    <span>Loading...</span>
                  </>
                ) : hasMore ? (
                  <span>Load previous items</span>
                ) : (
                  <span>No more</span>
                )}
              </button>
            </div>
          )}
        </>
      )}

      {/* Add/Edit Modal - portaled so header is below top bar */}
      {showAddModal && createPortal(
        <div className="fixed inset-0 bg-black bg-opacity-70 z-[100] flex items-start justify-center p-4 pt-20 overflow-y-auto">
          <div className="bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full my-auto max-h-[calc(100vh-5rem)] flex flex-col overflow-hidden border border-gray-700">
            <div className="flex-shrink-0 bg-gray-800 border-b border-gray-700 px-6 py-4">
              <div className="flex items-center justify-between">
              <h2 className="text-xl font-semibold text-white">
                {editingItem ? 'Edit Gallery Item' : 'Add New Gallery Item'}
              </h2>
              <button
                type="button"
                onClick={() => {
                  if (uploading) return
                  setShowAddModal(false)
                  setEditingItem(null)
                  setImageFile(null)
                  setImagePreview('')
                  setUploadPhase(null)
                  setUploadProgress(0)
                }}
                disabled={uploading}
                className="text-gray-400 hover:text-white disabled:opacity-50 disabled:cursor-not-allowed"
                aria-label="Close"
              >
                <X className="w-6 h-6" />
              </button>
              </div>
            </div>

            {/* Upload progress bar - visible while uploading/saving */}
            {uploading && (
              <div className="flex-shrink-0 px-6 py-3 bg-gray-800 border-b border-gray-700 space-y-2">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-300">
                    {uploadPhase === 'uploading' ? 'Uploading file...' : uploadPhase === 'saving' ? 'Saving to gallery...' : 'Please wait...'}
                  </span>
                  {uploadPhase === 'uploading' && (
                    <span className="text-white font-medium">{uploadProgress}%</span>
                  )}
                </div>
                <div className="h-2 w-full bg-gray-700 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-gradient-to-r from-purple-500 to-pink-500 transition-all duration-300 ease-out"
                    style={{ width: uploadPhase === 'saving' ? '100%' : `${uploadProgress}%` }}
                  />
                </div>
                <p className="text-xs text-gray-400">Do not close this window until the upload is complete.</p>
              </div>
            )}

            <form onSubmit={handleSubmit} className="p-6 space-y-4 overflow-y-auto flex-1 min-h-0">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Media (Image or Video) *
                </label>
                {imagePreview && (
                  <div className="mb-4 relative">
                    {formData.mediaType === 'video' ? (
                      <video
                        src={imagePreview}
                        className="w-full h-64 object-cover rounded-lg"
                        controls
                        preload="metadata"
                      />
                    ) : (
                      <img
                        src={imagePreview}
                        alt="Preview"
                        className="w-full h-64 object-cover rounded-lg"
                      />
                    )}
                  </div>
                )}
                <input
                  type="file"
                  accept="image/*,video/*"
                  onChange={handleMediaSelect}
                  className="w-full px-4 py-2 bg-gray-700 text-white rounded-lg border border-gray-600 focus:border-blue-500 focus:outline-none"
                />
                <p className="text-xs text-gray-400 mt-1">
                  Images: Max 10MB (JPG, PNG, WebP). Videos: Max 100MB (MP4, WebM, MOV)
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Title *
                </label>
                <input
                  type="text"
                  name="title"
                  value={formData.title}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-2 bg-gray-700 text-white rounded-lg border border-gray-600 focus:border-blue-500 focus:outline-none"
                  placeholder="Enter image title"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Description
                </label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  rows={3}
                  className="w-full px-4 py-2 bg-gray-700 text-white rounded-lg border border-gray-600 focus:border-blue-500 focus:outline-none"
                  placeholder="Enter image description (optional)"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Category *
                </label>
                <select
                  name="category"
                  value={formData.category}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-2 bg-gray-700 text-white rounded-lg border border-gray-600 focus:border-blue-500 focus:outline-none"
                >
                  {categories.map(cat => (
                    <option key={cat} value={cat}>{cat}</option>
                  ))}
                </select>
              </div>

              <div className="flex items-center">
                <input
                  type="checkbox"
                  name="isActive"
                  id="isActive"
                  checked={formData.isActive}
                  onChange={handleInputChange}
                  className="w-4 h-4 text-blue-600 bg-gray-700 border-gray-600 rounded focus:ring-blue-500"
                />
                <label htmlFor="isActive" className="ml-2 text-sm text-gray-300">
                  Active (visible on main site)
                </label>
              </div>

              <div className="flex items-center space-x-3 pt-4">
                <button
                  type="submit"
                  disabled={uploading}
                  className="flex-1 btn-primary flex items-center justify-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {uploading ? (
                    <>
                      <Loader className="w-5 h-5 animate-spin" />
                      <span>{uploadPhase === 'saving' ? 'Saving...' : 'Uploading...'}</span>
                    </>
                  ) : (
                    <>
                      <Upload className="w-5 h-5" />
                      <span>{editingItem ? 'Update' : 'Add'} Gallery Item</span>
                    </>
                  )}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    if (uploading) return
                    setShowAddModal(false)
                    setEditingItem(null)
                    setImageFile(null)
                    setImagePreview('')
                    setUploadPhase(null)
                    setUploadProgress(0)
                  }}
                  disabled={uploading}
                  className="px-6 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>,
        document.body
      )}
    </div>
  )
}

export default Gallery

